import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Progressbar, Toggle } from 'framework7-react';
import { FC, Fragment, ReactElement, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { orderBy, uniq, uniqBy } from 'lodash';
import pluralize from 'pluralize';
import { KING_OF_DRAG_ENABLED } from 'src/constants/FeatureFlags';

import API from '@api/API';

import {
  Entertainer,
  useCurrentEntertainerQuery,
  useCurrentUserEntertainerCritiqueQuery,
  useEntertainerCritiqueDataQuery,
  useEntertainerDirectory,
} from '@queries/EntertainerQueries';
import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import { Series, useSeriesByTagSlug, useSeriesEntertainersQuery } from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useCritiqueDocumentMutation } from '@mutations/CritiqueMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';
import { useIsVisible } from '@hooks/useIsVisible';
import { useShareLink } from '@hooks/useShareLink';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button, { ButtonProps } from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { Chip } from '@components/chips/Chip';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { AddFeaturedVideoPopup } from '@components/overlays/AddFeaturedVideoPopup';

import { magazineSvgUrl } from '@screens/series/SeriesScreen';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';
import {
  SweepstakesCard,
  useSweepstakesReferralLink,
} from '@screens/series/SeriesScreen/SweepstakesCard';

import stageIconUrl from '../../CommunityScreen/assets/stage.svg';
import { EntertainerRewiewsPopup } from '../EntertainerDetailsScreen/EntertainerReviews';
import { FeaturedKing } from '../EntertainerDetailsScreen/assets/FeaturedKing';
import { LandingPageSweepstakesCard } from '../EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import { FeaturedLook, useFeaturedLooksQuery } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import { MultiSelectField } from '../components/CitiesField';
import { AddFeaturedPhotoPopup } from '../components/FeaturedPhotoManagementSection';
import { FeatureForm } from './FeatureForm';
import { InstagramHandlePopup } from './InstagramHandlePopup';
import { KingOfDragBanner } from './KingOfDragBanner';
import { ManageShoutouts } from './ManageShoutouts';
import { ScheduledSocialPostSection } from './ScheduledSocialPost';
import { TopKingMaterialSection } from './TopKingMaterialSection';
import tickets from './tickets.svg';
import { useCityDragKingSeriesEntertainers } from './useCityDragKingSeriesEntertainers';
import { useCityDragKingSeriesEvents } from './useCityDragKingSeriesEvents';
import { useCityDragKingSeriesMostBookedEntertainers } from './useCityDragKingSeriesMostBookedEntertainers';

const MILESTONES = [5, 10, 15, 25, 40, 60, 100, 150, 250, 400, 600, 1000, 1500, 2000, 2500];

const USE_PROGRESS_BAR = true;
const GREEN = '#31f207';
const YELLOW = '#fedb41';
const RED = '#e13026';

const VoteMilestoneProgress: FC<{
  eventVoteCount: number;
}> = ({ eventVoteCount }) => {
  const [initialVoteCount] = useState(eventVoteCount);

  const currentGoal = useMemo(() => {
    return MILESTONES.find((milestone) => initialVoteCount < milestone);
  }, [initialVoteCount]);

  const percentageOfGoal = eventVoteCount / currentGoal;

  const chartColor = useMemo(() => {
    const percentageInt = Math.ceil(percentageOfGoal * 100);

    if (percentageInt > 50) {
      return GREEN;
    }

    if (percentageInt > 35) {
      return YELLOW;
    }

    return RED;
  }, [percentageOfGoal]);

  return (
    <div>
      <Progressbar
        style={
          {
            '--f7-progressbar-progress-color': chartColor,
          } as any
        }
        progress={percentageOfGoal * 100}
      />
      <div className="mt-1">
        {eventVoteCount ? (
          <>
            {eventVoteCount === currentGoal ? (
              <strong>Goal reached! 🔥🔥🔥</strong>
            ) : (
              <>
                <strong>{`${eventVoteCount} shoutouts`}</strong> of {currentGoal} goal
              </>
            )}
          </>
        ) : (
          <strong style={{ borderLeft: `2px solid ${RED}`, paddingLeft: 4 }}>
            Support another drag king!
          </strong>
        )}
      </div>
    </div>
  );
};

interface DragKingCommunityScreenProps {}

interface DragKingCommunitySubmission {
  _id: string;
  title: string;
  image?: string;
  status: string;
  entertainer?: {
    _id: string;
    title: string;
    bookingHandle: string;
  };
}

const useDragKingCommunitySubmissions = (isEnabled: boolean) => {
  const { data, isFetched } = useQuery({
    queryKey: ['dragKingCommunitySubmissions'],
    queryFn: () =>
      API.get<{ submissions: DragKingCommunitySubmission[] }>(
        `/v1/drag-king-community/submissions`,
      ),
    enabled: isEnabled,
  });

  return {
    submissions: data?.submissions,
    areSubmissionsReady: isFetched,
  };
};

const useUpdateDragKingCommunitySubmission = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerMediaId,
      status,
      temp,
    }: {
      entertainerMediaId: string;
      status: 'approved' | 'rejected';
      temp?: {
        entertainerInstagram?: string;
        base64Image?: any;
        path?: string;
        title?: string;
        addCollaborator?: boolean;
      };
    }) =>
      API.post(`/v1/drag-king-community/submissions`, {
        entertainerMediaId,
        status,
        temp,
      }),
  });

  return {
    isUpdatingSubmission: isPending,
    updateSubmissionAsync: mutateAsync,
  };
};

const DragKingSubmissionCard: FC<{
  submission: DragKingCommunitySubmission;
}> = ({ submission }) => {
  const [status, setStatus] = useState(submission.status);
  const [title, setTitle] = useState(submission.entertainer?.title);
  const { updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleApprove = async () => {
    setStatus('approved');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'approved',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  const handleReject = async () => {
    setStatus('rejected');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'rejected',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  return (
    <div className="d-grid gap-2">
      <img
        src={submission.image}
        alt={submission.title}
        className="w-100"
        style={{ borderRadius: 8 }}
      />
      <Input
        defaultValue={submission.entertainer.title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'approved' ? 'default' : 'outlined'}
          onClick={handleApprove}
        >
          Approve
        </Button>
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'rejected' ? 'default' : 'outlined'}
          onClick={handleReject}
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopupContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [entertainerInstagram, setInstagram] = useState('');
  const [base64Image, setUpdatedImage] = useState<string | ArrayBuffer>();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('approved');
  const [addCollaborator, setAddCollaborator] = useState(true);

  const { isUpdatingSubmission, updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleSubmit = async () => {
    await updateSubmissionAsync({
      entertainerMediaId: '',
      status: status as any,
      temp: {
        entertainerInstagram,
        base64Image,
        title,
        addCollaborator,
      },
    });

    onClose();
  };

  return (
    <div className="d-grid gap-2">
      <Select
        options={[
          { value: 'approved', label: 'Approved' },
          { value: 'pending', label: 'Pending' },
        ]}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      />
      <Input
        placeholder="Stage Name *"
        name="stageName"
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        placeholder="Instagram *"
        name="instagram"
        onChange={(event) => setInstagram(event.target.value)}
      />
      <div className="d-flex gap-2 align-items-center">
        <Toggle defaultChecked={addCollaborator} onToggleChange={setAddCollaborator} />
        <div>
          <strong>Add Collaborator</strong>
        </div>
      </div>
      <Card>
        <ImageUploadField
          name="customSubmissionImage"
          placeholder="Select an image"
          onChange={(blob) => setUpdatedImage(blob)}
        />
      </Card>
      <div>
        <Button
          roundness="rounded"
          disabled={!base64Image || !title || isUpdatingSubmission}
          onClick={handleSubmit}
        >
          {isUpdatingSubmission ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopup: FC<{ isVisible: boolean; onClose: () => void }> = ({
  isVisible,
  onClose,
}) => {
  return (
    <Popup title="Add Custom" isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && <AddCustomDragKingSubmissionPopupContent onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};

const useShareEntertainerMediaPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ entertainerMediaId, title }: { entertainerMediaId: string; title?: string }) =>
      API.post(`/v1/entertainer-media/${entertainerMediaId}/share-badges-post`, {
        title,
      }),
  });

  return {
    isSharing: isPending,
    shareEntertainerMediaPostAsync: mutateAsync,
  };
};

const BadgeCountCard: FC<{ featuredLook: FeaturedLook }> = ({ featuredLook }) => {
  const rootImageUrl = `https://gaggl.app/api/v1/entertainer-media/${featuredLook._id}/badges-post.jpg`;
  const [title, setTitle] = useState<string>();
  const [imageUrl, setImageUrl] = useState(rootImageUrl);
  const [isShared, setIsShared] = useState(false);

  const { shareEntertainerMediaPostAsync } = useShareEntertainerMediaPostMutation();

  const handleShare = async () => {
    if (!isShared) {
      setIsShared(true);
      await shareEntertainerMediaPostAsync({
        entertainerMediaId: featuredLook._id,
        title,
      });
    }
  };

  return (
    <div className="d-grid gap-2">
      <img src={imageUrl} alt={featuredLook.title} className="w-100" style={{ borderRadius: 8 }} />
      <Input defaultValue={featuredLook.title} onChange={(e) => setTitle(e.target.value)} />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant="outlined"
          onClick={() => {
            setImageUrl(`${rootImageUrl}?title=${encodeURIComponent(title)}`);
          }}
        >
          Update Title
        </Button>
        <Button roundness="rounded" variant="outlined" color="neutral" onClick={handleShare}>
          {isShared ? 'Shared' : 'Share Image'}
        </Button>
      </div>
    </div>
  );
};

const ShareBadgeCountContent = () => {
  const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({});

  const [end, setEnd] = useState(5);

  const kingsThingsLooks = useMemo(
    () => featuredLooks.filter(({ entertainer }) => entertainer.isDragKingThing),
    [featuredLooks],
  );

  return (
    <>
      <TitleToolbar text="Latest Badges Shared" size="sm" />

      <div className="d-grid gap-4">
        {kingsThingsLooks.slice(0, Math.min(end, kingsThingsLooks.length)).map((featuredLook) => (
          <BadgeCountCard key={featuredLook._id} featuredLook={featuredLook} />
        ))}
        <Button
          roundness="rounded"
          variant="smooth"
          color="neutral"
          onClick={() => setEnd((prevValue) => prevValue + 5)}
        >
          Load More
        </Button>
      </div>
    </>
  );
};

const SHARE_OPTIONS = [
  { value: 'join', label: 'Entertainers' },
  { value: 'look', label: 'Add Look' },
  // { value: 'badge', label: 'Reward Badges' },
  { value: 'show', label: 'Add Show' },
];

const MostBookedCard: FC<{ cityId: string; back: string }> = ({ cityId, back }) => {
  const { navigate } = useRouter();
  const handleClick = () => {
    navigate(`/noauth/drag-king-shoutouts/${cityId}/most-booked`, {
      state: { back },
    });
  };

  const { areCastEntertainersReady, castEntertainers, events } =
    useCityDragKingSeriesMostBookedEntertainers(cityId);

  return (
    <div className="p-3" style={{ background: '#009688bb', borderRadius: 8 }} onClick={handleClick}>
      <div className="fs-5">
        <strong>Booked & Busy Performers</strong>
      </div>
      <div className="mb-2">
        {areCastEntertainersReady
          ? `${events.length} upcoming shows. ${castEntertainers.length} kings cast.`
          : '--'}
      </div>
      <div style={{ fontSize: '.7rem' }}>See details →</div>
    </div>
  );
};

const EntertainerDirectorySections: FC<{ cityId: string; back: string }> = ({ cityId, back }) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();

  const { entertainerDirectory } = useEntertainerDirectory(cityId);

  const featuredIconSize = 24;

  return (
    <>
      {/* <TitleToolbar text="Local Recommendations" size="sm" /> */}
      <div className="mt-3">
        <div className="d-grid gap-2 mb-3">
          {entertainerDirectory?.sections?.map((section) => (
            <div
              className="d-flex align-items-center gap-2 p-2"
              style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
              onClick={() => {
                track('Featured Row Clicked', section);
                navigate(section.to, {
                  state: { back, forceBack: true },
                });
              }}
            >
              <div
                style={{
                  height: featuredIconSize,
                  width: featuredIconSize,
                }}
              >
                <img
                  className="d-block"
                  src={section.imgUrl}
                  alt={section.title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 2,
                  }}
                />
              </div>
              <div>
                <strong>{section.title}</strong>
              </div>
              <div className="ml-auto px-2 d-flex align-items-center gap-3">
                <div>{section.count ?? 0}</div>
                <FontAwesomeIcon name="chevron-right" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export const CitySelector: FC<{
  isInCitySelectorMode: boolean;
  hideMessage?: boolean;
  cityId: string;
  onChangeCityId: (updatedCityId: string) => void;
  onContinue: () => void;
  onIsProcessingCity: (instagramHandle: string) => void;
}> = ({
  hideMessage,
  isInCitySelectorMode,
  cityId,
  onChangeCityId,
  onContinue,
  onIsProcessingCity,
}) => {
  const { entertainer } = useCurrentEntertainerQuery();
  const { citiesAreReady, cities = [] } = useCitiesQuery();
  const { openAlertDialog } = useDialog();
  const { track } = useAnalytics();
  const [instagramHandle, setInstagramHandle] = useState<string>(entertainer?.contact?.instagram);
  const [isInstagramHandlePopupVisible, setIsInstagramHandlePopupVisible] = useState(false);
  const [missingCityName, setMissingCityName] = useState('');

  const submitMissingCity = (name: string, handle: string) => {
    track('Submit Missing City', {
      name,
      instagramHandle: handle,
    });
    openAlertDialog({
      title: 'City submitted',
      text: `Thank you for submitting! We will review it and add it to the list of cities soon.`,
      cb: () => {
        onIsProcessingCity(handle);
      },
    });
  };

  const CityEmptyState: FC<{ searchValue: string; onClose: () => void }> = ({
    searchValue,
    onClose,
  }) => {
    const handleMissingCity = () => {
      track('Handle Missing City', {
        searchValue,
      });

      if (instagramHandle) {
        submitMissingCity(searchValue, instagramHandle);
        return;
      }

      setMissingCityName(searchValue);
      setIsInstagramHandlePopupVisible(true);
    };

    return (
      <div className="p-2 text-align-center">
        <p className="mb-0">
          <strong>{searchValue ? `No matches for "${searchValue}"` : 'No matches'}</strong>
        </p>
        <p className="mt-0">Don't see your city? Don't worry, you can still continue!</p>
        <Button
          variant="smooth"
          color="neutral"
          roundness="rounded"
          onClick={() => {
            handleMissingCity();
            onClose();
          }}
        >
          Continue
        </Button>
      </div>
    );
  };

  return (
    <>
      {!hideMessage && (
        <p>
          {isInCitySelectorMode
            ? 'Which city would you like to see?'
            : 'To get started, select your primary city:'}
        </p>
      )}

      {cities.length ? (
        <div className="d-grid gap-2">
          <MultiSelectField
            allowSearch
            isSingleSelect
            options={cities
              .filter(({ eventsAreEnabled }) => eventsAreEnabled)
              .map(({ _id, name }) => ({
                value: _id,
                label: name,
              }))}
            defaultValue={cityId ? [cityId] : []}
            placeholder="--"
            name="cityId"
            onChange={(updatedValues) => {
              const [updatedCityId] = updatedValues;
              track('Update city selector', { updatedCityId });
              onChangeCityId(updatedCityId);
            }}
            EmptyStateComponent={CityEmptyState}
          />

          {/* <Select
          name="cityId"
          value={cityId}
          options={[
            { value: '', label: '--' },
            ...cities
              .filter(({ eventsAreEnabled }) => eventsAreEnabled)
              .map(({ _id, name }) => ({
                value: _id,
                label: name,
              })),
          ]}
          onChange={(e) => {
            onChangeCityId(e.target.value);
          }}
        /> */}

          <Button roundness="rounded" disabled={!cityId} onClick={onContinue}>
            Continue
          </Button>
          {/* <Button roundness="rounded" variant="flat" color="neutral" onClick={handleMissingCity}>
          I don't see my city
        </Button> */}
        </div>
      ) : (
        <Skeleton height={40} />
      )}

      <InstagramHandlePopup
        isVisible={isInstagramHandlePopupVisible}
        content="We'll use it to add to the city once we're finished reviewing it."
        onClose={() => setIsInstagramHandlePopupVisible(false)}
        onSubmit={(handle) => {
          setInstagramHandle(handle);
          submitMissingCity(missingCityName, handle);
        }}
      />
    </>
  );
};

const SupportEntertainerActions: FC<{
  entertainerId: string;
  isLiked: boolean;
  iconButtonProps?: {
    color?: ButtonProps['color'];
    variant?: ButtonProps['variant'];
  };
  counts: {
    likes: number;
    comments: number;
  };
  onCommentClick?: () => void;
}> = ({ entertainerId, iconButtonProps, isLiked: initialIsLike, counts, onCommentClick }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const [updatedIsLiked, setUpdatedIsLiked] = useState<boolean | undefined>();
  const { critiqueDocumentAsync } = useCritiqueDocumentMutation();

  const isLiked = typeof updatedIsLiked === 'boolean' ? updatedIsLiked : initialIsLike;

  const actionButtons: {
    isVisible: boolean;
    icon: ReactElement;
    onClick: any;
    count: number;
  }[] = [
    {
      isVisible: true,
      icon: (
        <FontAwesomeIcon
          name={isLiked ? 'heart' : 'heart-outline'}
          className={isLiked ? 'is-liked' : ''}
          style={{ color: isLiked ? '#e91e63' : undefined }}
        />
      ),
      onClick: async (mouseEvent: MouseEvent) => {
        mouseEvent.stopPropagation();
        const updatedValue = !isLiked;
        setUpdatedIsLiked(updatedValue);
        track('Toggle Entertainer Like', { entertainerId, updatedValue });
        await critiqueDocumentAsync({
          documentType: 'entertainers',
          documentId: entertainerId,
          primaryCritique: updatedValue ? 'like' : 'neutral',
        });
      },
      count: counts.likes + (updatedIsLiked && !initialIsLike ? 1 : 0),
    },
    {
      isVisible: !!onCommentClick,
      icon: <FontAwesomeIcon name="comment-outline" />,
      onClick: (mouseEvent: MouseEvent) => {
        mouseEvent.stopPropagation();
        track('Comment Icon Clicked');
        onCommentClick?.();
      },
      count: counts.comments,
    },
  ];

  return (
    <div className="border-0 pt-0">
      <div className="d-flex gap-4">
        {actionButtons
          .filter(({ isVisible }) => isVisible)
          .map(({ icon, count, onClick }, i) => (
            <div key={`action-${i}`} onClick={onClick} className="d-flex align-items-center gap-2">
              <div style={{ fontSize: 14 }}>{icon}</div>
              <div>
                <span>{count}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export const SupportEntertainerCard: FC<{
  entertainer: Entertainer;
  cityId: string;
  back: string;
  onCommentClick?: () => void;
  onNavigateCallback?: () => void;
}> = ({ entertainer, back, onCommentClick, onNavigateCallback }) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const divRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(divRef);
  const { userEntertainerCritique } = useCurrentUserEntertainerCritiqueQuery(
    entertainer._id,
    isVisible,
  );

  const image = entertainer.avatar || entertainer.featuredMedia?.[0]?.image;
  const imageSize = 3 * 18;

  return (
    <Card>
      <div ref={divRef}>
        <Card.Header
          className="p-2"
          avatar={
            <div
              style={{
                position: 'relative',
                width: imageSize,
                height: imageSize,
              }}
            >
              <div style={{ borderRadius: 16, overflow: 'hidden', width: '100%', height: '100%' }}>
                {image ? (
                  <AsyncImage
                    src={image}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    placeholder={
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'rgba(255,255,255,.2)',
                        }}
                      />
                    }
                  />
                ) : (
                  <div
                    style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,.2)' }}
                  />
                )}
              </div>
            </div>
          }
          tagline={entertainer.cities.map((city) => city.name).join(' • ')}
          title={entertainer.title}
          subtitle={[
            `${entertainer.badgeCount} ${pluralize('Badge', entertainer.badgeCount)}`,
            `${entertainer.featuredMedia.length} ${pluralize('Look', entertainer.featuredMedia.length)}`,
            // `${entertainer.details.categories.length} ${pluralize('Category', entertainer.details.categories.length)}`,
          ].join(' • ')}
          action={
            <IconButton
              size="sm"
              color="neutral"
              variant="smooth"
              onClick={() => {
                track('Follow Entertainer Card');
                navigate(`/noauth/entertainer/${entertainer.bookingHandle}`, {
                  state: {
                    back,
                  },
                });
                onNavigateCallback?.();
              }}
            >
              <FontAwesomeIcon name="chevron-right" />
            </IconButton>
          }
        />
        <div className="d-flex px-3 pb-2 align-items-center">
          <div>
            <SupportEntertainerActions
              entertainerId={entertainer._id}
              isLiked={userEntertainerCritique?.primaryCritique === 'like'}
              counts={{
                likes: entertainer.likeCount ?? 0,
                comments: entertainer.commentCount ?? 0,
              }}
              iconButtonProps={{ variant: 'flat' }}
              onCommentClick={onCommentClick}
            />
          </div>
          {onCommentClick && (
            <div
              className="ml-auto"
              onClick={() => {
                track('See All Support Clicked');
                onCommentClick();
              }}
            >
              <div>
                <u>See all support</u>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const ShareSection: FC<{}> = ({}) => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();
  const url = `https://bookdragkings.com`;

  const handleShareClick = async () => {
    track('Click share voting link inline');
    await shareLink({
      url,
    });
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2 mb-3"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ maxWidth: 'calc(100% - 75px)' }}>
        <div>
          <FontAwesomeIcon name="link" />
        </div>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</div>
        </div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Copy'}
        </Button>
      </div>
    </div>
  );
};

const MostSupportedEntertainersGridItem: FC<{ entertainer: Entertainer; onClick: () => void }> = ({
  entertainer,
  onClick,
}) => {
  const [didClick, setDidClick] = useState(false);

  const handleClick = () => {
    setDidClick(true);
    onClick();
  };

  return (
    <div
      key={entertainer._id}
      className="w-100"
      style={{
        position: 'relative',
        background: 'rgba(255,255,255,.1)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
      onClick={handleClick}
    >
      <div className="px-1 pb-1">
        <div
          className="py-1"
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <strong>{entertainer.title}</strong>
        </div>
        <div style={{ position: 'relative' }}>
          <img
            src={entertainer.avatar || entertainer.featuredMedia?.[0]?.image}
            alt={entertainer.title}
            style={{
              borderRadius: 3,
              width: '100%',
              height: '100%',
              aspectRatio: '4/5',
              objectFit: 'cover',
              display: 'block',
              overflow: 'hidden',
            }}
          />
          <div
            className="d-flex gap-2 align-items-center"
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              color: 'white',
              textShadow: '0 0 4px black',
            }}
          >
            <div className="d-flex align-items-center gap-1">
              <FontAwesomeIcon name="comment-outline" />
              <span>{entertainer.commentCount ?? 0}</span>
            </div>
            {Boolean(entertainer.unviewedCommentCount && !didClick) && (
              <div style={{ margin: '-8px 0', textShadow: 'none' }}>
                <Chip
                  size="sm"
                  color="red"
                  label={<strong>{`${entertainer.unviewedCommentCount} unread`}</strong>}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MostSupportedEntertainersGrid: FC<{
  entertainers: Entertainer[];
  onClickEntertainer?: (entertainerId: string) => void;
}> = ({ entertainers, onClickEntertainer }) => {
  return (
    <div
      className="d-grid"
      style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px', margin: '0 -1rem' }}
    >
      {entertainers?.map((entertainer) => (
        <MostSupportedEntertainersGridItem
          key={entertainer._id}
          entertainer={entertainer}
          onClick={() => {
            onClickEntertainer?.(entertainer._id);
          }}
        />
      ))}
    </div>
  );
};

const MostSupported: FC<{
  series?: Series;
  onClickEntertainer: (entertainerId: string) => void;
}> = ({ series, onClickEntertainer }) => {
  const { track } = useAnalytics();
  const {
    isFetched: seriesEntertainersAreReady,
    entertainers: seriesEntertainers = [],
    refetch: refetchSeriesEntertainers,
  } = useSeriesEntertainersQuery(series?._id, Boolean(series?.isEntertainerSeries));

  const top3 = useMemo(() => {
    return orderBy(
      seriesEntertainers.filter(
        (entertainer) => entertainer.avatar || entertainer.featuredMedia?.[0]?.image,
      ),
      ['commentCount', 'badgeCount', 'likeCount'],
      ['desc', 'desc', 'desc'],
    ).slice(0, 3);
  }, [seriesEntertainers]);

  const handleClick = (entertainerId: string) => {
    track('Click Most Supported Entertainer', { entertainerId });
    onClickEntertainer(entertainerId);
  };

  if (!seriesEntertainersAreReady) {
    return <Skeleton height={160} />;
  }

  if (top3.length !== 3) {
    return <></>;
  }

  return (
    <>
      {/* <TitleToolbar className="mt-0 mb-0" size="sm" text="Most Supported" />
      <p className="mt-0">
        These kings & things have gotten the most love from the community recently through comments,
        likes, and badges!
      </p> */}

      <p className="mt-0">
        <strong>Most Supported</strong> — These kings & things have gotten the most love from the
        community recently through comments, likes, and badges!
      </p>

      <MostSupportedEntertainersGrid entertainers={top3} onClickEntertainer={handleClick} />
    </>
  );
};

const CtaSection: FC<{
  entertainer: Entertainer;
  onAddPhotoClick: () => void;
  onAddVideoClick: () => void;
}> = ({ entertainer, onAddPhotoClick, onAddVideoClick }) => {
  const photoCount =
    entertainer?.featuredMedia?.filter((media: any) => media.type === 'image')?.length ?? 0;

  const videoCount =
    entertainer?.featuredMedia?.filter((media: any) => media.type === 'video')?.length ?? 0;

  return (
    <div className="d-grid gap-2 mb-3">
      <PrimaryCtaCard
        cta={{
          img: magazineSvgUrl,
          title: `Share a look for a feature?`,
          description: `Upload your best photo — this one's your spotlight.`,
          url: '',
          badgeCount: photoCount ? undefined : 1,
          onClick: onAddPhotoClick,
        }}
        track={{
          event: `Add ${photoCount ? 'another' : 'your first'} photo`,
        }}
      />
      <PrimaryCtaCard
        cta={{
          img: stageIconUrl,
          title: `Let's see you in action`,
          description: `Send a clip and show us what the stage already knows.`,
          url: '',
          badgeCount: videoCount ? undefined : 1,
          onClick: onAddVideoClick,
        }}
        track={{
          event: `Add video`,
        }}
      />
    </div>
  );
};

const VIEW_AS_USER = true;

enum TabValue {
  Default = 'default',
  Looks = 'looks',
  Badges = 'badges',
  ManageShoutouts = 'manageShoutouts',
}

interface DragKingCommunityScreenProps {}

interface DragKingShoutoutsScreenQueryParams {
  cityId?: string;
  tab?: TabValue;
  addImageModeIsVisible?: 'true';
  didSubmitImage?: 'true';
  addVideoModeIsVisible?: 'true';
  didSubmitVideo?: 'true';
}

export const DragKingShoutoutsScreen = createScreenComponent<
  DragKingCommunityScreenProps,
  DragKingShoutoutsScreenQueryParams
>(({ f7route }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const [cityId, setCityId] = useState<string>(f7route.query?.cityId);
  const [didSubmitCity, setDidSubmitCity] = useState(!!f7route.query?.cityId);
  const [isInCitySelectorMode, setIsInCitySelectorMode] = useState(false);
  const { currentUser, refetchUser } = useCurrentUser();
  const { entertainer } = useCurrentEntertainerQuery();
  const [instagramHandle, setInstagramHandle] = useState<string>(
    entertainer?.contact?.instagram ?? '',
  );
  const { city } = useCityById(cityId ?? entertainer?.cityIds?.[0]);
  const { submissions = [] } = useDragKingCommunitySubmissions(!!entertainer?._id);
  const [featureFormIsVisible, setFeatureFormIsVisible] = useState<{
    isVisible: boolean;
    didSubmitImage?: boolean;
  }>({
    isVisible: f7route.query?.addImageModeIsVisible === 'true',
    didSubmitImage: f7route.query?.didSubmitImage === 'true',
  });
  const [addVideoModeIsVisible, setAddVideoModeIsVisible] = useState<{
    isVisible: boolean;
    message?: string;
    didSubmitVideo?: boolean;
  }>({
    isVisible: f7route.query?.addVideoModeIsVisible === 'true',
    didSubmitVideo: f7route.query?.didSubmitVideo === 'true',
  });
  const [addCustomSubmissionModeIsVisible, setAddCustomSubmissionModeIsVisible] = useState(false);
  const [tab, setTab] = useState<TabValue>(f7route.query?.tab ?? TabValue.Default);
  const [currentReviewsEntertainerId, setCurrentReviewsEntertainerId] = useState<string>();

  const { isFetched: isSeriesReady, series } = useSeriesByTagSlug(
    city?.featuredSeries?.kingsThings,
  );

  const {
    isFetched: seriesEntertainersAreReady,
    entertainers: seriesEntertainers = [],
    refetch: refetchSeriesEntertainers,
  } = useSeriesEntertainersQuery(series?._id, Boolean(series?.isEntertainerSeries));

  const currentPath = `/noauth/drag-king-shoutouts?cityId=${city?._id}`;

  const SupportEntertainerCardWithCommentAction: FC<{
    entertainer: Entertainer;
    cityId: string;
    back: string;
  }> = (props) => {
    return (
      <SupportEntertainerCard
        {...props}
        onCommentClick={() => setCurrentReviewsEntertainerId(props.entertainer._id)}
      />
    );
  };

  return (
    <Screen
      name="Drag King Community Screen"
      hideToolbar
      headerProps={{
        title: <Screen.GagglTitle />,
        right: <ShareLinkButton url={`https://bookdragkings.com`} />,
      }}
    >
      <Screen.Content>
        <ShareSection />

        <div className="mb-3">
          <KingOfDragBanner context="Drag King Directory" />
        </div>

        <div className="d-flex mb-3 gap-3 align-items-center">
          <FeaturedKing height={48} width={48} />
          <div>
            <strong className="fs-5">Kings & Things Directory</strong>
            <br />
            <span>Calling all drag kings & drag things! 👑</span>
          </div>
        </div>

        <p className="mt-0">
          <strong>Book Drag Kings</strong> is here to celebrate the drag artists who don't always
          get the spotlight — kings, things, and everything in between. Drop your photo or upcoming
          show here and get a shoutout. This is your space.
        </p>

        {/* <TopKingMaterialSection context="Drag King Directory" /> */}

        {!isInCitySelectorMode && (didSubmitCity || entertainer?._id) ? (
          <>
            {!featureFormIsVisible.isVisible ? (
              <>
                <CtaSection
                  entertainer={entertainer}
                  onAddPhotoClick={() => {
                    setFeatureFormIsVisible({ isVisible: true });
                  }}
                  onAddVideoClick={() => {
                    setAddVideoModeIsVisible({ isVisible: true });
                  }}
                />

                {currentUser && (
                  <Card>
                    <div className="pt-3 pb-2 px-3">
                      <VoteMilestoneProgress
                        eventVoteCount={currentUser.sharedEntertainerCommentCount}
                      />
                      <div className="mt-1">Share some love for your fellow kings & things!</div>
                    </div>
                    <Card.Footer>
                      <Button
                        roundness="rounded"
                        color="neutral"
                        variant="smooth"
                        onClick={() =>
                          setFeatureFormIsVisible({ isVisible: true, didSubmitImage: true })
                        }
                      >
                        Share some shoutouts
                      </Button>
                    </Card.Footer>
                  </Card>
                )}

                {KING_OF_DRAG_ENABLED && (
                  <LinkBanner
                    icon={
                      <div className="d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon className="fs-2" name="camera-retro" />
                      </div>
                    }
                    title={`King of Drag Cast Picks`}
                    description={`See the looks favorited by Buck Wylde and Pressure K, cast members of King of Drag!`}
                    onClick={() => {}}
                  />
                )}

                {!city ? (
                  <>
                    <LinkBanner
                      icon={
                        <div className="d-flex align-items-center justify-content-center">
                          <FontAwesomeIcon name="certificate fs-2" />
                        </div>
                      }
                      title="View Trending Looks"
                      description={`See the latest looks & share badges`}
                      onClick={() => {
                        navigate('/engagement/looks');
                      }}
                    />
                  </>
                ) : (
                  <>
                    {/* <div className="d-flex gap-2 align-items-center justify-content-between mt-4 mb-2">
                        <div className="fs-6">
                          <strong>{city?.name}</strong>
                        </div>
                        <div>
                          <Button
                            className="py-1 px-3"
                            size="sm"
                            roundness="rounded"
                            variant="smooth"
                            color="neutral"
                            onClick={() => {
                              setIsInCitySelectorMode(true);
                            }}
                          >
                            Change City
                          </Button>
                        </div>
                      </div>

                      <div
                        style={{
                          marginBottom: currentUser?.isDragKingCommunityAdmin ? 8 : undefined,
                        }}
                      >
                        <LandingPageSweepstakesCard cityId={city?._id} />
                      </div> */}

                    {currentUser?.isDragKingCommunityAdmin && !VIEW_AS_USER && (
                      <ButtonFilter
                        selected={tab}
                        options={[
                          { value: TabValue.Default, label: 'Default' },
                          { value: TabValue.Looks, label: 'Looks' },
                          { value: TabValue.Badges, label: 'Badges' },
                        ]}
                        onChange={(value) => setTab(value as TabValue)}
                      />
                    )}

                    {tab === TabValue.Default && (
                      <>
                        {/* <MostBookedCard cityId={city?._id} back={currentPath} /> */}
                        {/* <EntertainerDirectorySections cityId={city?._id} back={currentPath} /> */}
                        <TitleToolbar
                          size="md"
                          text={city?.name}
                          action={
                            <Button
                              className="py-1 px-2"
                              size="sm"
                              roundness="rounded"
                              variant="smooth"
                              color="neutral"
                              onClick={() => {
                                setIsInCitySelectorMode(true);
                              }}
                            >
                              Change City
                            </Button>
                          }
                        />

                        {isSeriesReady ? (
                          <>
                            <MostSupported
                              series={series}
                              onClickEntertainer={setCurrentReviewsEntertainerId}
                            />

                            <TitleToolbar
                              size="sm"
                              text={'All Local Kings & Things'}
                              action={<span>{`${seriesEntertainers.length} total`}</span>}
                            />
                            <SeriesEntertainersList
                              series={series}
                              city={city}
                              back={currentPath}
                              viewAsUser={VIEW_AS_USER}
                              joinCategories={['Drag King']}
                              joinCallback={() => {}}
                              CardComponent={SupportEntertainerCardWithCommentAction}
                            />
                          </>
                        ) : (
                          <>
                            <Skeleton height={40} />
                            <Skeleton height={40} />
                            <Skeleton height={40} />
                            <Skeleton height={40} />
                            <Skeleton height={40} />
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                {currentUser?.isDragKingCommunityAdmin && (
                  <>
                    {tab === TabValue.Badges && <ShareBadgeCountContent />}

                    {tab === TabValue.Looks && (
                      <>
                        <TitleToolbar
                          text="Pending Submissions"
                          size="sm"
                          action={<span>{`${submissions.length} total`}</span>}
                        />

                        <div className="mb-4">
                          <Button
                            roundness="rounded"
                            variant="flat"
                            color="neutral"
                            onClick={() => {
                              setAddCustomSubmissionModeIsVisible(true);
                            }}
                          >
                            Add Custom Submission
                          </Button>
                        </div>

                        <div className="d-grid gap-4">
                          {submissions.map((submission) => (
                            <DragKingSubmissionCard
                              key={submission._id ?? submission.image}
                              submission={submission}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {tab === TabValue.ManageShoutouts && (
                      <div className="mt-4">
                        <ManageShoutouts />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <FeatureForm
                  instagramHandle={instagramHandle}
                  didSubmitImage={featureFormIsVisible.didSubmitImage ?? false}
                  cityId={city?._id || ''}
                  backUrl={currentPath}
                  onCancel={() => {
                    setFeatureFormIsVisible({ isVisible: false });
                  }}
                  onAddVideoClick={() => {
                    setAddVideoModeIsVisible({ isVisible: true });
                  }}
                />
              </>
            )}
          </>
        ) : (
          <CitySelector
            isInCitySelectorMode={isInCitySelectorMode}
            cityId={cityId}
            onChangeCityId={setCityId}
            onContinue={() => {
              track('Select City', {
                cityName: city?.name,
              });
              setDidSubmitCity(true);
              setIsInCitySelectorMode(false);
              setTab(TabValue.Default);
            }}
            onIsProcessingCity={(submittedInstagramHandle) => {
              setInstagramHandle(submittedInstagramHandle);
              setDidSubmitCity(true);
            }}
          />
        )}
      </Screen.Content>
      <AddCustomDragKingSubmissionPopup
        isVisible={addCustomSubmissionModeIsVisible}
        onClose={() => setAddCustomSubmissionModeIsVisible(false)}
      />
      <EntertainerRewiewsPopup
        isVisible={!!currentReviewsEntertainerId}
        entertainerId={currentReviewsEntertainerId}
        entertainerTitle={
          seriesEntertainers.find((e) => e._id === currentReviewsEntertainerId)?.title || ''
        }
        headerElement={
          <SupportEntertainerCard
            entertainer={seriesEntertainers.find((e) => e._id === currentReviewsEntertainerId)!}
            cityId={city?._id || ''}
            back={currentPath}
            onNavigateCallback={() => setCurrentReviewsEntertainerId(undefined)}
          />
        }
        onClose={() => setCurrentReviewsEntertainerId(undefined)}
      />
      <AddFeaturedVideoPopup
        isVisible={addVideoModeIsVisible.isVisible}
        onClose={() => {
          setAddVideoModeIsVisible({ isVisible: false });
        }}
        cityId={city?._id || ''}
        message={addVideoModeIsVisible.message}
        meta={{ channel: 'dragkingcommunity', channelAction: 'addVideo' }}
      />
    </Screen>
  );
});
